import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import CalendarModal from '../components/CalendarModal';
import WhatsAppButton from '../components/WhatsAppButton';

function Contact() {
  const [activeForm, setActiveForm] = useState('enquiry'); // 'enquiry' or 'partnership'
  const [isCalendarModalOpen, setIsCalendarModalOpen] = useState(false);

  const forms = {
    enquiry: {
      id: 'fs_e987f768aea41db299f44540eceb634082ad62741daccca8e71b5fa74b81e5f6',
      src: 'https://pbww.myfreshworks.com/crm/sales/web_forms/e987f768aea41db299f44540eceb634082ad62741daccca8e71b5fa74b81e5f6/form.js',
      label: 'Events Enquiry'
    },
    partnership: {
      id: 'fs_d59f06ce35c529522257605cdb80309e3273852684d1896785eaad43eb4a4b7d',
      src: 'https://pbww.myfreshworks.com/crm/sales/web_forms/d59f06ce35c529522257605cdb80309e3273852684d1896785eaad43eb4a4b7d/form.js',
      label: 'Partnership Enquiry'
    }
  };

  useEffect(() => {
    // Helper function to inject script
    const injectFormScript = (formConfig, containerId) => {
      if (document.getElementById(formConfig.id)) return; // Already injected

      const script = document.createElement('script');
      script.src = formConfig.src;
      script.id = formConfig.id;
      script.crossOrigin = "anonymous";
      
      const container = document.getElementById(containerId);
      if (container) {
        container.appendChild(script);
      }
    };

    // Inject BOTH forms immediately on mount
    injectFormScript(forms.enquiry, 'freshworks-crm-container-enquiry-contact');
    injectFormScript(forms.partnership, 'freshworks-crm-container-partnership-contact');

    // Cleanup on final unmount
    return () => {
      Object.values(forms).forEach(form => {
        const script = document.getElementById(form.id);
        if (script && script.parentNode) {
          script.parentNode.removeChild(script);
        }
      });
      
      const injectedContainers = document.querySelectorAll('.fs-webform-container');
      injectedContainers.forEach(el => el.remove());
      const injectedLoaders = document.querySelectorAll('.fs-webform-loader');
      injectedLoaders.forEach(el => el.remove());
      
      delete window.WebFormQueue;
    };
  }, []); // Run only once on mount

  return (
    <div className="min-h-screen py-12 sm:py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"> 
        {/* Enquiry Form Section */}
        <div className="mb-12 sm:mb-20">
          <div className="text-center mb-8 sm:mb-16">
            <h2 className="section-title text-2xl sm:text-3xl lg:text-4xl">Get in Touch</h2>
            <p className="section-subtitle text-base sm:text-lg">Fill out the form below and our team will get back to you shortly</p>
          </div>
          
          <div className="max-w-3xl mx-auto relative bg-white rounded-xl shadow-lg border border-gray-100 p-1 min-h-[750px] flex flex-col">
            {/* Selection Buttons at the Top of the container */}
            <div className="px-6 py-8 border-b border-gray-100 flex flex-col sm:flex-row justify-center items-center gap-4 sm:gap-6 bg-gray-50/50 rounded-t-xl">
              <button
                type="button"
                role="button"
                aria-label="Select Events Enquiry form"
                data-form-target="enquiry"
                onClick={() => setActiveForm('enquiry')}
                className={`w-full sm:w-auto min-w-[200px] min-h-[44px] px-6 py-3 rounded-lg font-medium text-base transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 active:scale-[0.98] flex items-center justify-center ${
                  activeForm === 'enquiry'
                    ? 'bg-primary-600 text-white shadow-md transform translate-y-[-2px]'
                    : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200 cursor-pointer shadow-sm'
                }`}
              >
                {activeForm === 'enquiry' && <span className="mr-2">✓</span>}
                Events Enquiry
              </button>
              <button
                type="button"
                role="button"
                aria-label="Select Partnership Enquiry form"
                data-form-target="partnership"
                onClick={() => setActiveForm('partnership')}
                className={`w-full sm:w-auto min-w-[200px] min-h-[44px] px-6 py-3 rounded-lg font-medium text-base transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 active:scale-[0.98] flex items-center justify-center ${
                  activeForm === 'partnership'
                    ? 'bg-primary-600 text-white shadow-md transform translate-y-[-2px]'
                    : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200 cursor-pointer shadow-sm'
                }`}
              >
                {activeForm === 'partnership' && <span className="mr-2">✓</span>}
                Partnership Enquiry
              </button>
            </div>

            {/* Form Containers with Transitions */}
            <div className="relative flex-grow overflow-hidden">
              <motion.div
                key="enquiry-form-contact"
                initial={{ opacity: 0, x: -20 }}
                animate={{ 
                  opacity: activeForm === 'enquiry' ? 1 : 0, 
                  x: activeForm === 'enquiry' ? 0 : -20,
                  display: activeForm === 'enquiry' ? 'block' : 'none'
                }}
                transition={{ duration: 0.3, ease: "easeInOut" }}
                className="w-full h-full"
                data-form-id="enquiry-contact"
                data-active-form={activeForm === 'enquiry'}
              >
                <div id="freshworks-crm-container-enquiry-contact" className="w-full h-full"></div>
              </motion.div>

              <motion.div
                key="partnership-form-contact"
                initial={{ opacity: 0, x: 20 }}
                animate={{ 
                  opacity: activeForm === 'partnership' ? 1 : 0, 
                  x: activeForm === 'partnership' ? 0 : 20,
                  display: activeForm === 'partnership' ? 'block' : 'none'
                }}
                transition={{ duration: 0.3, ease: "easeInOut" }}
                className="w-full h-full"
                data-form-id="partnership-contact"
                data-active-form={activeForm === 'partnership'}
              >
                <div id="freshworks-crm-container-partnership-contact" className="w-full h-full"></div>
              </motion.div>
            </div>
          </div>
        </div>
        
        {/* Contact Information Section */}
        <div className="text-center mb-8 sm:mb-12 lg:mb-16">
          <h1 className="section-title text-2xl sm:text-3xl lg:text-4xl">Contact Information</h1>
          <p className="section-subtitle text-base sm:text-lg">Get in touch with us directly</p>
        </div>
        
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
            <div className="card p-6 text-center">
              <div className="flex justify-center mb-4">
                <svg className="h-8 w-8 text-primary-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold mb-2">Email</h3>
              <p className="text-gray-600">sales@photobookworldwide.com</p>
            </div>
            
            <div className="card p-6 text-center">
              <div className="flex justify-center mb-4">
                <svg className="h-8 w-8 text-primary-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold mb-2">Phone</h3>
              <p className="text-gray-600">+6012-4124909</p>
            </div>
            
            <div className="card p-6 text-center">
              <div className="flex justify-center mb-4">
                <svg className="h-8 w-8 text-primary-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold mb-2">Address</h3>
              <p className="text-gray-600">7, Jln TP 2, Sime UEP Industrial Park, 47600 Subang Jaya, Selangor</p>
            </div>
          </div>
        </div>
        
        {/* WhatsApp Business Section */}
        <div className="mt-16 sm:mt-20">
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="section-title text-2xl sm:text-3xl lg:text-4xl">Connect on WhatsApp</h2>
            <p className="section-subtitle text-base sm:text-lg">Scan the QR code to start a conversation</p>
          </div>
          
          <div className="max-w-md mx-auto">
            <div className="card p-6 sm:p-8 text-center">
              <div className="mb-6">
                <img 
                  src="/images/whatsapp-qr.png" 
                  alt="WhatsApp Business QR Code" 
                  className="w-64 h-64 mx-auto rounded-lg shadow-lg"
                />
              </div>
              <h3 className="text-xl font-semibold mb-3 text-gray-800">Photobook Enterprise</h3>
              <p className="text-gray-600 mb-4">WhatsApp Business Account</p>
              <p className="text-sm text-gray-500">Scan with your phone's camera or WhatsApp to connect instantly</p>
            </div>
          </div>
        </div>
      </div>
      
      <CalendarModal isOpen={isCalendarModalOpen} onClose={() => setIsCalendarModalOpen(false)} />
      <WhatsAppButton />
    </div>
  );
}

export default Contact;